/**
 * printer-twin-plugin — the `/printer-twin` interactive 3D digital-twin surface as a
 * studio-sibling `ComposableGenomePlugin` (#563, absorbing #564).
 *
 * Mirrors `devicePlugin`/`cadPlugin`: one `component` editor (`printer-twin`) routed
 * at `/printer-twin`, lowering onto `component`/`route` only (NO new `EntityKind` —
 * a Device twin is a genome DOMAIN-PATTERN over the #515 device model, not an engine
 * primitive; gate 4 stays green, the route count derives). The editor is
 * `React.lazy` so the whole R3F twin stack (Canvas + three + drei + the device
 * adapters) stays in a split chunk OUT of the app entry (gate 8 — the eager module
 * carries only the route + the `React.lazy` boundary, the `device-plugin.tsx`
 * precedent).
 *
 * @module @ubiclouds/v5-studio/device/twin/printer-twin-plugin
 */

import { lazy } from 'react';
import { definePlugin } from '@ubiclouds/v5-sdk/plugins';
import type { ComposableGenomePlugin } from '@ubiclouds/v5-sdk/plugins';

/** The printer-twin surface plugin id. */
export const PRINTER_TWIN_PLUGIN_ID = 'v5-studio-printer-twin';

/** The split-chunk interactive 3D digital-twin surface (the `/printer-twin` editor). */
export const LazyPrinterTwinSurface = lazy(() => import('./PrinterTwinSurface.js'));

/**
 * The printer-twin plugin — the `/printer-twin` route → the lazy `printer-twin`
 * editor. Composed as a SIBLING in `studioPlugins`; it adds no new `EntityKind` (the
 * surface lowers onto `component`/`route`), so gate 4 stays green.
 */
export const printerTwinPlugin: ComposableGenomePlugin = definePlugin(PRINTER_TWIN_PLUGIN_ID)
  .name('Printer twin')
  .trustTier('core')
  .editor('printer-twin', LazyPrinterTwinSurface)
  .route('/printer-twin', { routesTo: 'component/printer-twin', title: 'Printer twin' })
  .build();
